import { useAnalytics } from '@app/analytics/use-analytics';
import { useTrackedLinkHandler } from '@app/analytics/utils';
import { DialogPrompt } from '@app/components/dialog-prompt/dialog-prompt';
import { Spinner } from '@app/components/icons/spinner';
import { FLAG_SPACES } from '@app/feature-flags/flags';
import { useFlags } from '@app/hooks/use-flags';
import {
  selectCurrentSpaceExternalId,
  selectInitialSpaceIdResolved,
} from '@app/routes/organization/selectors';
import { useAppDispatch, useAppSelector } from '@app/store';
import { useOrganizationId } from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { ChangeEvent, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { NoFilterResults } from '../../components/no-filter-results';
import { SearchInput } from '../../components/search-input';
import { Layout } from '../../layout';
import {
  selectContentPages,
  selectIsPagesLoaded,
  selectPageDeletionInfo,
  selectPageSearchText,
} from '../../selectors';
import { buildCanvasEditorUrl } from '../utils';
import { Introduction } from './components/introduction';
import { PageCard } from './components/page-card';

const PageList = () => {
  const { status, data, pagination } = useAppSelector(selectContentPages);
  const {
    isDialogOpen,
    pageExternalId,
    pageTitle,
    status: deletionStatus,
  } = useAppSelector(selectPageDeletionInfo);
  const searchText = useAppSelector(selectPageSearchText);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { track } = useAnalytics();

  const hasNoPages = !data || data.length === 0;
  const isPagesLoaded = useAppSelector(selectIsPagesLoaded);
  const isLoadingPages = status === 'processing';

  const resetPageDeleteConfirmation = useCallback(() => {
    dispatch({ type: '[content] page delete cancelled' });
  }, [dispatch]);

  const handleDelete = useCallback(() => {
    if (!pageExternalId) return;
    dispatch({
      type: '[content] page delete started',
      payload: {
        pageExternalId,
      },
    });
    track('ch_hubs_delete');
  }, [dispatch, pageExternalId, track]);

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch({
        type: '[content] page change clicked',
        payload: {
          page,
        },
      });
    },
    [dispatch],
  );

  if (!isPagesLoaded) return <Spinner size="large" />;

  if (status === 'failed')
    return <div>{t('content-hub.failed-to-load-pages')}</div>;

  if (isLoadingPages) return <Spinner size="large" />;
  if (hasNoPages && !searchText) return <Introduction />;
  if (hasNoPages && searchText) return <NoFilterResults />;

  return (
    <>
      <div className="grid grid-cols-5 gap-2">
        {data?.map(page => (
          <PageCard key={page.externalId} page={page} />
        ))}
      </div>
      {pagination && pagination.totalPages > 0 ? (
        <div className="flex items-center justify-center gap-4 my-4">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="px-3 py-1 text-sm text-blue-400  disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('content-hub.previous-page')}
          >
            {t('content-hub.previous')}
          </button>
          <div className="text-sm py-1">
            {t('content-hub.page-of-total-pages', {
              page: pagination.page,
              totalPages: pagination?.totalPages,
            })}
          </div>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
            className="px-3 py-1 text-sm text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('content-hub.next-page')}
          >
            {t('content-hub.next')}
          </button>
        </div>
      ) : null}
      <DialogPrompt
        isShown={isDialogOpen}
        onClose={resetPageDeleteConfirmation}
        onConfirm={handleDelete}
        title={`${t('content-hub.delete')} ${pageTitle}?`}
        description={t('content-hub.delete-page-description')}
        isLoading={deletionStatus === 'processing'}
        confirmText={t('content-hub.delete')}
        cancelText={t('content-hub.cancel')}
      />
    </>
  );
};

export const Pages = () => {
  const organizationId = useOrganizationId(window.location.pathname);
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const searchText = useAppSelector(selectPageSearchText);
  const flags = useFlags();
  const flagsResolved = flags !== undefined;
  const spacesEnabled = Boolean(flags?.[FLAG_SPACES]);
  const initialSpaceIdResolved = useAppSelector(selectInitialSpaceIdResolved);
  const spaceExternalId = useAppSelector(selectCurrentSpaceExternalId);
  // Until feature flags and the initial selected space resolve, spaceExternalId
  // can be transiently empty in a spaces-enabled org — hide the link rather
  // than risk a click sending the user to Canvas without space_external_id.
  // initialSpaceIdResolved can also settle "resolved" with no space found at
  // all (dashboard-app.tsx dispatches resolved:true once loading finishes,
  // regardless of whether a space was found), so require spaceExternalId too.
  const isCreateHubLinkReady =
    flagsResolved &&
    (!spacesEnabled || (initialSpaceIdResolved && Boolean(spaceExternalId)));
  const createHubUrl = buildCanvasEditorUrl(
    `/org/${organizationId}/page/create`,
    {
      spacesEnabled,
      spaceExternalId,
    },
  );

  const { track } = useAnalytics();

  const trackSearch = useDebouncedCallback(() => {
    track('ch_hubs_search');
  }, 500);

  const handleSearchChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: '[content] update search text',
        payload: {
          searchText: evt.target.value,
        },
      });
      trackSearch();
    },
    [dispatch, trackSearch],
  );

  const handleSearchClear = useCallback(() => {
    dispatch({
      type: '[content] clear search text',
    });
    trackSearch();
  }, [dispatch, trackSearch]);

  const handleCreateHubClick = useTrackedLinkHandler('ch_hubs_create_hub', {
    source: 'top_of_page',
  });

  return (
    <Layout
      contextMenu={
        <>
          <SearchInput
            value={searchText}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
          />

          <a
            href={isCreateHubLinkReady ? createHubUrl : undefined}
            onClick={isCreateHubLinkReady ? handleCreateHubClick : undefined}
            aria-disabled={!isCreateHubLinkReady}
            className="w-max rounded-lg bg-primary text-sm inline-flex items-center justify-center text-white px-4  py-2 font-medium outline-none hover:text-white hover:bg-blue-600 no-underline"
          >
            {t('content-hub.new')}
          </a>
        </>
      }
    >
      <PageList />
    </Layout>
  );
};
